import { useState } from "react";
import Header from "@/components/layout/header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useQuery } from "@tanstack/react-query";
import { 
  Building,
  Building2,
  Users, 
  ShoppingCart,
  ShoppingBag,
  CheckCircle,
  ChevronRight,
  Eye,
  Clock,
  Ticket,
  Settings,
  HeadphonesIcon,
  CalendarDays,
  Check,
  AlertCircle,
  Languages,
  Package
} from "lucide-react";

// Type definitions for API data
interface OnboardingStep {
  id: number;
  stepNumber: number;
  title: string;
  completed: boolean;
  current: boolean;
}

interface QuickAction {
  id: number;
  title: string;
  description: string;
  icon: string;
  color: string;
  href: string;
}

interface DashboardTicket {
  id: number;
  ticketId: string;
  subject: string;
  tenant: string;
  status: string;
  timestamp: string;
}

interface UpcomingTask {
  id: number;
  task: string;
  tenant: string;
  dueDate: string;
  status: string;
}

// Helper function to format KPI data with real values
const createKpiData = (stats?: { activeTenants: number; activeClients: number; activeMarketplaceItems: number; pendingTranslations: number; totalPackages: number }) => [
  {
    title: "Active Clients",
    value: stats?.activeClients?.toString() || "0",
    description: "Total number of active client organizations",
    icon: Building,
    color: "text-primary",
    bgColor: "bg-primary bg-opacity-10"
  },
  {
    title: "Active Tenants",
    value: stats?.activeTenants?.toString() || "0",
    description: "All active tenant organizations",
    icon: Users,
    color: "text-blue-600 dark:text-blue-400",
    bgColor: "bg-blue-100 dark:bg-blue-900 dark:bg-opacity-20"
  },
  {
    title: "Marketplace Items",
    value: stats?.activeMarketplaceItems?.toString() || "0",
    description: "Available templates, plugins, and themes",
    icon: ShoppingCart,
    color: "text-green-600 dark:text-green-400",
    bgColor: "bg-green-100 dark:bg-green-900 dark:bg-opacity-20"
  },
  {
    title: "Total Packages",
    value: stats?.totalPackages?.toString() || "0",
    description: "Total packages setup in global marketplace",
    icon: Package,
    color: "text-purple-600 dark:text-purple-400",
    bgColor: "bg-purple-100 dark:bg-purple-900 dark:bg-opacity-20"
  },
  {
    title: "Pending Translations",
    value: stats?.pendingTranslations?.toString() || "0",
    description: "Translations awaiting approval",
    icon: Languages,
    color: "text-emerald-600 dark:text-emerald-400",
    bgColor: "bg-emerald-100 dark:bg-emerald-900 dark:bg-opacity-20"
  }
];

// Helper function to map icon names to components
const getIconComponent = (iconName: string) => {
  const iconMap: Record<string, any> = {
    Building2,
    Languages,
    Ticket,
    ShoppingBag,
    Building,
    Clock,
    Settings,
    HeadphonesIcon
  };
  return iconMap[iconName] || Building;
};

const getStatusColor = (status: string) => {
  switch (status) {
    case "open": return "bg-red-100 text-red-800 dark:bg-red-900 dark:bg-opacity-20 dark:text-red-400";
    case "in-progress": return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:bg-opacity-20 dark:text-blue-400";
    case "resolved": return "bg-green-100 text-green-800 dark:bg-green-900 dark:bg-opacity-20 dark:text-green-400";
    case "pending": return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:bg-opacity-20 dark:text-yellow-400";
    case "review": return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:bg-opacity-20 dark:text-purple-400";
    case "scheduled": return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300";
    default: return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300";
  }
};

const getTaskStatusIcon = (status: string) => {
  switch (status) {
    case "pending": return AlertCircle;
    case "review": return Eye;
    case "scheduled": return CalendarDays;
    default: return Clock;
  }
};

export default function Dashboard() {
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);
  
  // Fetch dashboard stats from API
  const { data: dashboardStats } = useQuery<{
    activeTenants: number;
    activeClients: number;
    activeMarketplaceItems: number;
    pendingTranslations: number;
    totalPackages: number;
  }>({
    queryKey: ['/api/v1/analytics/dashboard/stats']
  });

  // Fetch data from API
  const { data: onboardingSteps = [] } = useQuery<OnboardingStep[]>({
    queryKey: ['/api/v1/analytics/onboarding-steps']
  });

  const { data: quickActions = [] } = useQuery<QuickAction[]>({
    queryKey: ['/api/v1/analytics/quick-actions']
  });

  const { data: recentTickets = [] } = useQuery<DashboardTicket[]>({
    queryKey: ['/api/v1/admin/support/tickets?limit=5']
  });

  const { data: upcomingTasks = [] } = useQuery<UpcomingTask[]>({
    queryKey: ['/api/v1/analytics/upcoming-tasks']
  });

  // Map quick actions to the expected format
  const quickActionTiles = quickActions.map((action) => ({
    id: action.id,
    title: action.title,
    description: action.description,
    icon: getIconComponent(action.icon),
    href: action.href,
    color: action.color
  }));
  
  const currentStep = onboardingSteps.find((step) => step.current);
  const completedSteps = onboardingSteps.filter((step) => step.completed).length;
  const progressPercentage = onboardingSteps.length > 0 ? (completedSteps / onboardingSteps.length) * 100 : 0;
  
  // Create KPI data with real values from API
  const kpiData = createKpiData(dashboardStats);

  return (
    <div className="flex flex-col h-full">
      <Header
        title="Welcome to Athlyn CMS"
        description="Your central hub to manage clients, vendors, and operations"
      />
      
      <div className="flex-1 overflow-auto p-6 space-y-6">
        {/* KPI Strip */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {kpiData.map((kpi, index) => {
            const IconComponent = kpi.icon;
            
            return (
              <TooltipProvider key={index}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Card 
                      className={`athlyn-card transition-all duration-200 cursor-pointer ${
                        hoveredCard === index ? 'transform -translate-y-1 shadow-lg' : ''
                      }`}
                      onMouseEnter={() => setHoveredCard(index)}
                      onMouseLeave={() => setHoveredCard(null)}
                      role="region"
                      aria-label={`${kpi.title} metrics`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 ${kpi.bgColor} rounded-lg flex items-center justify-center`}>
                            <IconComponent className={`w-6 h-6 ${kpi.color}`} />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-muted-foreground">{kpi.title}</p>
                            <p className="text-2xl font-bold text-foreground">{kpi.value}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{kpi.description}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          })}
        </div>

        {/* Onboarding Wizard */}
        <Card className="athlyn-card" role="region" aria-label="Onboarding progress">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-primary" />
              Onboarding Progress
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Step {completedSteps + 1} of {onboardingSteps.length}</span>
                <span>{Math.round(progressPercentage)}% Complete</span>
              </div>
              <Progress value={progressPercentage} className="h-2" />
            </div>
            
            <div className="flex flex-wrap gap-4">
              {onboardingSteps.map((step, index) => (
                <div 
                  key={step.id} 
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                    step.completed 
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:bg-opacity-20 dark:text-green-400'
                      : step.current
                      ? 'bg-primary bg-opacity-10 text-white dark:text-white font-semibold'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {step.completed ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <span className="w-4 h-4 rounded-full border-2 border-current flex items-center justify-center text-xs">
                      {step.id}
                    </span>
                  )}
                  <span className="text-sm">{step.title}</span>
                </div>
              ))}
            </div>
            
            {currentStep && (
              <div className="flex items-center gap-4 pt-2">
                <Button className="athlyn-button-primary">
                  Continue Onboarding
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
                <p className="text-sm text-muted-foreground">
                  Next: {currentStep.title}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Action Tiles */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {quickActionTiles.map((tile, index) => {
            const IconComponent = tile.icon;
            
            return (
              <Card 
                key={tile.id}
                className="athlyn-card hover:shadow-lg transition-all duration-200 cursor-pointer group"
                onClick={() => window.location.href = tile.href}
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 ${tile.color} rounded-lg flex items-center justify-center transition-transform group-hover:scale-110`}>
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-foreground mb-2">{tile.title}</h3>
                      <p className="text-muted-foreground text-sm">{tile.description}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Recent Items Feed */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Latest Support Tickets */}
          <Card className="athlyn-card" role="region" aria-label="Recent support tickets">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Ticket className="w-5 h-5" />
                Latest Support Tickets
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 max-h-80 overflow-y-auto">
                {recentTickets.map((ticket) => (
                  <div key={ticket.id} className="flex items-start gap-4 p-3 rounded-lg hover:bg-muted hover:bg-opacity-50 transition-colors cursor-pointer">
                    <div className="w-10 h-10 bg-red-100 dark:bg-red-900 dark:bg-opacity-20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Ticket className="w-5 h-5 text-red-600 dark:text-red-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-sm font-semibold text-foreground truncate">{ticket.subject}</h4>
                        <Badge variant="outline" className={`text-xs ${getStatusColor(ticket.status)}`}>
                          {ticket.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mb-1">{ticket.tenant}</p>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Clock className="w-3 h-3" />
                        <span>{ticket.timestamp}</span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Upcoming Onboarding Tasks */}
          <Card className="athlyn-card" role="region" aria-label="Upcoming onboarding tasks">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarDays className="w-5 h-5" />
                Upcoming Tasks
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 max-h-80 overflow-y-auto">
                {upcomingTasks.map((task) => {
                  const StatusIcon = getTaskStatusIcon(task.status);
                  
                  return (
                    <div key={task.id} className="flex items-start gap-4 p-3 rounded-lg hover:bg-muted hover:bg-opacity-50 transition-colors cursor-pointer">
                      <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 dark:bg-opacity-20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <StatusIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-sm font-semibold text-foreground">{task.task}</h4>
                          <Badge variant="outline" className={`text-xs ${getStatusColor(task.status)}`}>
                            {task.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mb-1">{task.tenant}</p>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <CalendarDays className="w-3 h-3" />
                          <span>Due: {task.dueDate}</span>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>


      </div>
    </div>
  );
}